'use client'

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import ctl from '@netlify/classnames-template-literals'
import { addDays, getDate, getMonth, getYear, isAfter, isBefore, isSameDay, startOfMonth, startOfWeek } from 'date-fns'
import { ReactNode, useEffect, useState } from 'react'

type CalendarProps = {
	now: Date
	value: Date
	span: [Date, Date]
	onChange: (val: Date) => unknown
	toolbar?: ReactNode
	oldestLimit: Date
}

const monthLookup = [
	'JAN',
	'FEB',
	'MAR',
	'APR',
	'MAY',
	'JUN',
	'JUL',
	'AUG',
	'SEP',
	'OCT',
	'NOV',
	'DEC',
]

const range = (start: number, stop: number) => Array.from({ length: stop - start + 1 }, (_, i) => start + i)

// this is never going to break, surely nobody wouldn't refresh the page on new years
const currentYear = (new Date()).getFullYear()

export function Calendar({ now, value, onChange, span, toolbar, oldestLimit }: CalendarProps) {
	const yearsLookup = range(getYear(oldestLimit), currentYear)
	const [month, setMonth] = useState(getMonth(value))
	const [year, setYear] = useState(getYear(value))
	const start = startOfWeek(startOfMonth(new Date(year, month, 1, 0, 0)))

	// reset month/year when selection moves
	useEffect(() => {
		setMonth(getMonth(value))
		setYear(getYear(value))
	}, [value])

	const cells = Array.from({ length: 35 }).map((_, i) => addDays(start, i))

	return (
		<div className="grid grid-rows-[auto_1fr]">
			<div className="mt-4">
				<div className="flex items-stretch px-6 gap-3">
					<div className="flex items-center">
						{toolbar}
					</div>
					<div className="grow" />
					<div className="flex items-center">
						<select
							value={month}
							onChange={(e) => setMonth(parseInt(e.target.value))}
							className="border-none pl-1 pr-3 py-0.5 bg-none focus:outline-none focus:ring-0"
						>
							{monthLookup.map((m, i) => <option value={i} key={`${i}-month`}>{m}</option>)}
						</select>
						<div className="w-0">
							<ChevronDownIcon className="w-4 rotate-0 transition-all pointer-events-none -translate-x-3" />
						</div>
					</div>
					<div className="flex items-center">
						<select
							value={year}
							onChange={(e) => setYear(parseInt(e.target.value))}
							className="border-none pl-1 pr-3 py-0.5 bg-none focus:outline-none focus:ring-0"
						>
							{yearsLookup.map((y, i) => <option value={y} key={`${i}-year`}>{y}</option>)}
						</select>
						<div className="w-0">
							<ChevronDownIcon className="w-4 rotate-0 transition-all pointer-events-none -translate-x-3" />
						</div>
					</div>
				</div>
			</div>
			<div className="grid grid-cols-7 grid-rows-[repeat(1fr,6)] p-4 text-center">
				<div className="contents font-medium ">
					<div>Su</div>
					<div>Mo</div>
					<div>Tu</div>
					<div>We</div>
					<div>Th</div>
					<div>Fr</div>
					<div>Sa</div>
				</div>
				<div className="contents">
					{cells.map((d, i) => (
						<Day key={i} date={d} month={month} now={now} value={value} span={span} onChange={onChange} />
					))}
				</div>
			</div>
		</div>
	)
}

type DayProps = {
	date: Date // this particular date
	month: number
	now: Date // the anchor "now"
	onChange: (val: Date) => unknown

	span: [Date, Date] // calendar selection span
	value: Date | null // value of the calendar
}

function Day({ date, month, now, onChange, span, value }: DayProps) {
	const activeDateClassName = ctl('text-fuchsia-600')
	const inactiveDateClassName = ctl('text-black/50 text-xs')
	const inSpanInnerClassName = ctl('bg-fuchsia-100 text-center')
	const oppositeEndInnerClassName = ctl('text-white bg-fuchsia-200')
	const valueInnerClassName = ctl('bg-fuchsia-600 text-fuchsia-100 text-center')

	let className = ctl('py-2 grid items-stretch justify-stretch w-full')
	let innerClassName = ctl('px-2 flex items-center justify-center w-full')
	if (getMonth(date) === month) {
		className = `${className} ${activeDateClassName}`
	} else {
		className = `${className} ${inactiveDateClassName}`
	}

	if (isAfter(date, now)) {
		className = ctl(`opacity-25 ${className}`)
	}

	// error detection
	if (isBefore(span[1], span[0])) {
		console.error('Span is the wrong way around')
	}

	if (value) {
		if (isSameDay(span[0], date)) {
			if (isSameDay(date, value)) {
				innerClassName = ctl(`${innerClassName} ${valueInnerClassName}  rounded-l-lg`)
			} else {
				innerClassName = ctl(`${innerClassName} ${oppositeEndInnerClassName} rounded-l-lg`)
			}
		}

		if (isSameDay(span[1], date)) {
			if (isSameDay(date, value)) {
				innerClassName = ctl(`${innerClassName} ${valueInnerClassName}  rounded-r-lg`)
			} else {
				innerClassName = ctl(`${innerClassName} ${oppositeEndInnerClassName} rounded-r-lg`)
			}
		}

		if (isAfter(date, span[0]) && isBefore(date, span[1])) {
			innerClassName = ctl(`${innerClassName} ${inSpanInnerClassName} `)
		}
	}

	return (
		<button type="button" className={className} onClick={() => onChange(date)}>
			<span className={innerClassName}>
				<span className="block">{getDate(date)}</span>
			</span>
		</button>
	)
}
